<script setup lang="ts">
import { computed } from 'vue'
import { smartBezier } from '../../../../../shared/flow-core/src/geometry'
import { useFlowEditorContext } from '../composables/useFlowEditor'
import { WORLD_SIZE } from '../model/constants'
import { inputPoint, outputPoint } from '../model/ports'
import FlowNodeCard from './FlowNodeCard.vue'

/** Zoom bar and the pannable world with edges and nodes. */
const editor = useFlowEditorContext()
const { interactionTool } = editor.ui
const { nodes, edges, nodeById } = editor.document
const { selectedEdgeId, selectEdge } = editor.selection
const { viewport, canvasEl, gridStyle, zoomAt, resetView } = editor.viewport
const onCanvasMouseMove = editor.viewport.trackCursor
const onCanvasWheel = editor.viewport.onWheel
const {
  marquee: marqueeState,
  marqueeStyle,
  isCanvasDragOver,
  connectionPreviewPath,
  onCanvasMouseDown,
  onCanvasDragOver,
  onCanvasDragLeave,
  onCanvasDrop,
} = editor.pointer

/** Ports with an edge, keyed "nodeId:port:kind", for handle colouring. */
const connectedPortSet = computed(() => {
  const set = new Set<string>()
  for (const edge of edges.value) {
    set.add(`${edge.fromNodeId}:${edge.fromPort}:output`)
    set.add(`${edge.toNodeId}:${edge.toPort}:input`)
  }
  return set
})

const edgeViews = computed(() =>
  edges.value.flatMap((edge) => {
    const from = nodeById.value.get(edge.fromNodeId)
    const to = nodeById.value.get(edge.toNodeId)
    if (!from || !to) return []
    return [
      {
        id: edge.id,
        d: smartBezier(outputPoint(from, edge.fromPort), inputPoint(to, edge.toPort)),
        selected: selectedEdgeId.value === edge.id,
      },
    ]
  }),
)
</script>

<template>
  <section class="panel flow-canvas-wrap">
    <header class="panel__header flow-toolbar">
      <button type="button" class="pl-btn pl-btn--secondary" @click="zoomAt(1.15)">
        {{ $t('flow.zoomIn') }}
      </button>
      <button type="button" class="pl-btn pl-btn--secondary" @click="zoomAt(1 / 1.15)">
        {{ $t('flow.zoomOut') }}
      </button>
      <button type="button" class="pl-btn pl-btn--secondary" @click="resetView">
        {{ $t('flow.resetView') }}
      </button>
      <span class="flow-toolbar__hint"
        >{{ $t('flow.scale') }} {{ viewport.scale.toFixed(2) }}x</span
      >
    </header>

    <div
      ref="canvasEl"
      class="flow-canvas"
      :class="{
        'flow-canvas--drop-target': isCanvasDragOver,
        'flow-canvas--marquee': interactionTool === 'marquee',
      }"
      @mousedown="onCanvasMouseDown"
      @mousemove="onCanvasMouseMove"
      @wheel.prevent="onCanvasWheel"
      @dragover="onCanvasDragOver"
      @dragleave="onCanvasDragLeave"
      @drop="onCanvasDrop"
    >
      <div class="flow-grid" :style="gridStyle" aria-hidden="true" />
      <div
        v-if="marqueeState.active"
        class="marquee-rect"
        :style="marqueeStyle"
        aria-hidden="true"
      />
      <div
        class="flow-world"
        :style="{
          transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})`,
          transformOrigin: '0 0',
        }"
      >
        <svg class="flow-canvas__edges" :width="WORLD_SIZE" :height="WORLD_SIZE">
          <g>
            <path
              v-if="connectionPreviewPath"
              :d="connectionPreviewPath"
              class="flow-edge flow-edge--preview"
            />
            <path
              v-for="edge in edgeViews"
              :key="`${edge.id}-hit`"
              :d="edge.d"
              class="flow-edge-hit"
              @click.stop="selectEdge(edge.id)"
            />
            <path
              v-for="edge in edgeViews"
              :key="edge.id"
              :d="edge.d"
              class="flow-edge"
              :class="{ 'flow-edge--selected': edge.selected }"
            />
          </g>
        </svg>

        <FlowNodeCard
          v-for="node in nodes"
          :key="node.id"
          :node="node"
          :connected-ports="connectedPortSet"
        />
      </div>
    </div>
  </section>
</template>

<style scoped>
.flow-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.flow-toolbar__hint {
  color: #475569;
  font-size: 0.82rem;
}

.flow-canvas {
  position: relative;
  flex: 1;
  min-height: 0;
  border-top: 1px solid var(--pl-color-border-subtle, #e2e8f0);
  background: #f8fafc;
  overflow: hidden;
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.flow-canvas--marquee {
  cursor: default;
}

.flow-canvas--marquee:active {
  cursor: crosshair;
}

.flow-canvas--drop-target {
  box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--pl-color-primary, #4338ca) 55%, #ffffff);
}

.flow-canvas:not(.flow-canvas--marquee):active {
  cursor: grabbing;
}

.flow-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(to right, rgba(148, 163, 184, 0.24) 1.1px, transparent 1.1px),
    linear-gradient(to bottom, rgba(148, 163, 184, 0.24) 1.1px, transparent 1.1px);
  pointer-events: none;
  z-index: 0;
}

.flow-world {
  position: relative;
  width: 40000px;
  height: 40000px;
  z-index: 1;
  user-select: none;
}

.flow-canvas__edges {
  position: absolute;
  inset: 0;
}

.flow-edge-hit {
  fill: none;
  stroke: rgba(0, 0, 0, 0);
  stroke-width: 20;
  pointer-events: stroke;
}

.flow-edge {
  fill: none;
  stroke: #334155;
  stroke-width: 2.3;
  stroke-linecap: round;
  pointer-events: none;
}

.flow-edge--selected {
  stroke: #4338ca;
  stroke-width: 3.3;
}

.flow-edge--preview {
  stroke: #4338ca;
  stroke-width: 2.6;
  stroke-dasharray: 7 7;
  opacity: 0.9;
}

.marquee-rect {
  position: absolute;
  border: 2px solid rgba(59, 130, 246, 0.9);
  background: rgba(59, 130, 246, 0.12);
  border-radius: 0.4rem;
  pointer-events: none;
  z-index: 900;
}
</style>
